/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { Modal } from 'antd'
import { CloseOutlined } from '@ant-design/icons'
import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { ROUTES } from '@sportninja/common/constants/app'
import { RATE_CURRENCY_SYMBOL } from '@sportninja/common/features/playerFinder/constants'
import { NOT_IN_A_COMPETITION_GUIDANCE } from '@sportninja/common/features/profile/finderSettings/guidance'
import type {
  FinderPreference,
  FinderPreferenceInput,
} from '@sportninja/common/features/profile/finderSettings/types'
import {
  addSeed,
  editSeed,
  emptyForm,
  formFromPreference,
  formToInput,
  isFormValid,
  NO_LOCATION,
  toggleDay,
  toggleTime,
  type FinderForm,
  type SeedAddress,
} from '@sportninja/common/features/profile/finderSettings/finderPreferenceForm'
import Text from 'src/components/ts-components/Text'
import Button from 'src/components/ts-components/Button'
import { CountryAndProvinceInput } from 'src/components/ts-components/CountryAndProvinceInput'
import { SelectInput } from 'src/components/ts-components/SelectInput'
import { TextAreaInput } from 'src/components/ts-components/TextAreaInput'
import { TextInput } from 'src/components/ts-components/TextInput'
import { font, weightSemiBold, paragraphSmall } from 'src/components/css'
import { useFinderSportOptions } from '../hooks/useFinderSportOptions'
import { useSportName } from '../hooks/useSportName'
import SportSelectField from './SportSelectField'
import SkillLevelChips from './SkillLevelChips'
import DayChips from './DayChips'
import TimeChips from './TimeChips'

interface Props {
  isOpen: boolean
  onClose: () => void
  editingPreference: FinderPreference | null
  excludeSportIds: string[]
  genderId: number | null
  /** SN-8295 — the player's profile address: an Add's location starts from
   * it when it is complete. An Edit starts from the card's own location. */
  profileAddress?: SeedAddress | null
  onSubmit: (input: FinderPreferenceInput, prefId?: string) => void
  submitting: boolean
  fieldErrors: Record<string, string>
  onClearFieldError: (field: string) => void
}

// Matches the family Invite/Edit modal chrome: tight header/footer (horizontal
// padding only, no extra top/bottom), content padding owned by the body.
const modalStyles = {
  header: {
    backgroundColor: colors.SECONDARY_300,
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  body: {
    borderTop: `1px solid ${colors.SOFT_STEEL}`,
    borderBottom: `1px solid ${colors.SOFT_STEEL}`,
    backgroundColor: colors.SECONDARY_300,
  },
  content: {
    backgroundColor: colors.SECONDARY_300,
    paddingTop: '8px',
    paddingBottom: '16px',
    paddingLeft: '0',
    paddingRight: '0',
  },
  footer: {
    paddingLeft: '16px',
    paddingRight: '16px',
  },
  wrapper: { zIndex: 10001 },
  mask: { backgroundColor: 'rgba(6, 10, 16, 0.8)', zIndex: 10000 },
}

/**
 * Add/edit modal for a single Player Finder sport preference. The sport
 * picker is locked once editing an existing preference (the sport is
 * immutable after a listing exists); all other fields stay editable.
 */
const PreferenceModal: React.FC<Props> = ({
  isOpen,
  onClose,
  editingPreference,
  excludeSportIds,
  genderId,
  profileAddress,
  onSubmit,
  submitting,
  fieldErrors,
  onClearFieldError,
}) => {
  const isEdit = Boolean(editingPreference)
  const [form, setForm] = useState<FinderForm>(emptyForm())
  // What was typed in the State/Province field of a country without provinces.
  // It's shown, but it's no province, so it never reaches the form's provinceId.
  const [provinceText, setProvinceText] = useState('')
  // Once a province list loads, a province has to be picked from it. Stable:
  // the input reports its list from an effect keyed on this callback.
  const dropTypedProvince = useCallback((provinces: readonly unknown[]) => {
    if (provinces.length > 0) {
      setProvinceText('')
    }
  }, [])

  const seedSportName = useSportName(editingPreference)
  // Keep the sport being edited resolvable in the picker (its row is locked, but
  // its value must still exist in the options list). The effective id matches
  // how excludeSportIds is built: a generic pref's specific id, else sport_id.
  const editingSportId = editingPreference
    ? editingPreference.generic_sport_id || editingPreference.sport_id
    : undefined
  const { groups, resolveSport, positionsFor } = useFinderSportOptions(
    excludeSportIds.filter((id) => id !== editingSportId)
  )
  const positionOptions = positionsFor(form.sportId || null)

  // Reseed on target change (add vs a specific edit). SN-8295 — the location
  // is the card's own: an Edit starts from it (formFromPreference), an Add
  // from the profile address when complete (addSeed). The address is read as
  // primitives, so a refetched profile with the same address doesn't reseed a
  // form the player is filling in.
  const addressCity = profileAddress?.city
  const addressCountryId = profileAddress?.country?.id
  const addressProvinceId = profileAddress?.province?.id
  useEffect(() => {
    if (!isOpen) {
      return
    }
    setProvinceText('')
    if (editingPreference) {
      setForm({
        ...formFromPreference(editingPreference),
        sportName: seedSportName || editingPreference.sport_name,
      })
    } else {
      const { location } = addSeed({
        city: addressCity,
        country: { id: addressCountryId },
        province: { id: addressProvinceId },
      })
      setForm({ ...emptyForm(), ...location })
    }
    // seedSportName is derived from editingPreference; safe dep set.
  }, [
    isOpen,
    editingPreference,
    seedSportName,
    addressCity,
    addressCountryId,
    addressProvinceId,
  ])

  const handleSelectSport = (value: string) => {
    const resolved = resolveSport(value)
    if (!resolved) {
      return
    }
    onClearFieldError('sportId')
    setForm((f) => ({
      ...f,
      sportId: resolved.sportId,
      genericSportId: resolved.genericSportId,
      sportName: resolved.sportName,
      positionId: resolved.defaultPositionId,
      // `positionOptions` here still reflects the PREVIOUS sport (it's
      // derived from `form.sportId`, which hasn't updated yet) — resolving
      // the label from it would store a stale/wrong name. `positionName`
      // isn't read anywhere for display (the Position SelectInput renders
      // its own label from its own options) and isn't sent by `formToInput`,
      // so clear it here; the Position SelectInput's onChange sets it
      // correctly once the user picks (or re-picks) a position against the
      // fresh options for this sport.
      positionName: '',
    }))
  }

  const handleSubmit = () => {
    if (!isFormValid(form)) {
      return
    }
    // Only a change to where the form started is sent: the card's location on
    // an Edit, none on an Add (so a pre-filled address is sent as the card's).
    const seedLocation = editingPreference
      ? editSeed(editingPreference).location
      : NO_LOCATION
    onSubmit(
      formToInput(form, { genderId, seedLocation }),
      editingPreference?.id
    )
  }

  const sportSelectValue = form.genericSportId || form.sportId || undefined

  const footer = (
    <div css={footerStyle}>
      <Button
        text={capitalizeWords('Cancel')}
        variant='secondary'
        onClick={onClose}
        disabled={submitting}
      />
      <Button
        text={capitalizeWords(isEdit ? 'Save' : 'Add')}
        variant='primary'
        onClick={handleSubmit}
        isLoading={submitting}
        disabled={submitting || !isFormValid(form)}
      />
    </div>
  )

  if (!isOpen) {
    return null
  }

  return (
    <Modal
      width={600}
      open={isOpen}
      onCancel={onClose}
      centered
      maskClosable={false}
      closeIcon={
        <CloseOutlined
          css={css`
            color: ${colors.SOLITUDE};
          `}
        />
      }
      title={
        <Text variant='heading3' weight='semiBold' color={colors.WHITE}>
          {capitalizeWords(
            isEdit ? 'Edit Player Finder Profile' : 'Add Player Finder Profile'
          )}
        </Text>
      }
      footer={footer}
      styles={modalStyles}
    >
      <div css={bodyStyle}>
        <div css={rowStyle}>
          <div css={growStyle} data-testid='finder-sport-select'>
            <div css={fieldLabelStyle}>
              {capitalizeWords('Sport')}
              <span css={requiredMarkStyle}>*</span>
            </div>
            <SportSelectField
              groups={groups}
              value={sportSelectValue}
              onChange={handleSelectSport}
              disabled={isEdit}
              placeholder={capitalizeWords('Select a sport')}
            />
          </div>
          <div css={[growStyle, builtinFieldStyle]}>
            <SelectInput
              name='positionId'
              label={`${capitalizeWords('Preferred Position')} *`}
              options={positionOptions}
              value={form.positionId}
              disabled={!form.sportId}
              placeholder={capitalizeWords('Select a position')}
              onChange={(positionId) => {
                onClearFieldError('positionId')
                const match = positionOptions.find(
                  (o) => o.value === positionId
                )
                setForm((f) => ({
                  ...f,
                  positionId,
                  positionName: match?.label ?? '',
                }))
              }}
              fieldErrors={fieldErrors}
              customError={{}}
            />
          </div>
          <div css={rateStyle}>
            <div css={fieldLabelStyle}>{capitalizeWords('Rate')}</div>
            {/* The currency sits beside the value rather than only in the
                placeholder, so it stays visible once a rate is entered. */}
            <div css={rateInputWrapStyle}>
              <span css={rateCurrencyStyle} aria-hidden>
                {RATE_CURRENCY_SYMBOL}
              </span>
              <input
                css={rateInputStyle}
                inputMode='decimal'
                value={form.rate}
                aria-label={`Rate in ${RATE_CURRENCY_SYMBOL} per game`}
                placeholder='/ Game'
                onChange={(e) => {
                  onClearFieldError('charge_rate')
                  setForm((f) => ({ ...f, rate: e.target.value }))
                }}
              />
            </div>
            {fieldErrors.charge_rate ? (
              <div css={errorTextStyle}>{fieldErrors.charge_rate}</div>
            ) : null}
          </div>
        </div>

        {/* SN-8294 — the player plays this sport in no competition yet:
            explain, across the whole form, instead of showing the raw server
            sentence under the narrow Sport field. */}
        {fieldErrors.sportId ? (
          <div css={errorTextStyle} role='alert'>
            {NOT_IN_A_COMPETITION_GUIDANCE}{' '}
            <Link css={guidanceLinkStyle} to={ROUTES.TEAM_FINDER}>
              {capitalizeWords('Find a Team')}
            </Link>
          </div>
        ) : null}

        <div>
          <SkillLevelChips
            value={form.skillLevel}
            onChange={(skillLevel) => {
              onClearFieldError('skillLevel')
              setForm((f) => ({ ...f, skillLevel }))
            }}
          />
          {fieldErrors.skillLevel ? (
            <div css={errorTextStyle}>{fieldErrors.skillLevel}</div>
          ) : null}
        </div>

        <div>
          <DayChips
            value={form.days}
            onToggleDay={(day) => {
              onClearFieldError('days')
              setForm((f) => toggleDay(f, day))
            }}
          />
          {fieldErrors.days ? (
            <div css={errorTextStyle}>{fieldErrors.days}</div>
          ) : null}
        </div>

        <div>
          <TimeChips
            value={form.times}
            onToggleTime={(time) => {
              onClearFieldError('times')
              setForm((f) => toggleTime(f, time))
            }}
          />
          {fieldErrors.times ? (
            <div css={errorTextStyle}>{fieldErrors.times}</div>
          ) : null}
        </div>

        <div css={builtinFieldStyle}>
          <TextAreaInput
            name='bio'
            label={capitalizeWords('Player Bio')}
            value={form.bio}
            placeholder={capitalizeWords('Add a short bio for this sport')}
            setValue={(bio: string) => {
              onClearFieldError('bio')
              setForm((f) => ({ ...f, bio }))
            }}
            // TextAreaInput's internal `status` check unconditionally calls
            // `fieldErrors?.includes(name)` (array-shaped API), which throws
            // on our map-shaped fieldErrors ({ field: message }). Pass []
            // here and render the real message ourselves below (same pattern
            // as the rate field).
            fieldErrors={[]}
            customError={{}}
          />
          {fieldErrors.bio ? (
            <div css={errorTextStyle}>{fieldErrors.bio}</div>
          ) : null}
        </div>

        {/* SN-8295 — this card's own location, saved on this sport's card
            only (never another card, never the profile address). */}
        <div css={locationStyle}>
          <CountryAndProvinceInput
            countryValue={form.countryId || undefined}
            provinceValue={form.provinceId || provinceText || undefined}
            onCountryChange={(pickedCountryId) => {
              onClearFieldError('countryId')
              setProvinceText('')
              setForm((f) => ({
                ...f,
                countryId: pickedCountryId ?? '',
                provinceId: '',
              }))
            }}
            // A picked province comes with its id; typed text has none.
            onProvinceChange={(value, pickedProvinceId) => {
              onClearFieldError('provinceId')
              setProvinceText(pickedProvinceId === undefined ? value ?? '' : '')
              setForm((f) => ({
                ...f,
                provinceId:
                  pickedProvinceId === undefined
                    ? ''
                    : String(pickedProvinceId),
              }))
            }}
            onProvincesLoaded={dropTypedProvince}
            valueMode='id'
            provinceValueMode='id'
            allowClear
            responsive
            labels={{
              country: capitalizeWords('Country'),
              province: capitalizeWords('State/Province'),
            }}
            placeholders={{
              country: 'Select a Country',
              province: 'Select a State/Province',
            }}
            fieldErrors={{
              country: fieldErrors.countryId,
              province: fieldErrors.provinceId,
            }}
            customErrors={{
              country: fieldErrors.countryId,
              province: fieldErrors.provinceId,
            }}
          />
          <TextInput
            name='city'
            label={capitalizeWords('City')}
            value={form.city}
            setValue={(value: string) => {
              onClearFieldError('city')
              setForm((f) => ({ ...f, city: value }))
            }}
            placeholder={capitalizeWords('Enter a city')}
            fieldErrors={fieldErrors}
            customError={{}}
          />
        </div>
      </div>
    </Modal>
  )
}

export default PreferenceModal

// ─── Styles ────────────────────────────────────────────────────────────────

const bodyStyle = css`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
`

const rowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`

const growStyle = css`
  flex: 1 1 160px;
  min-width: 0;
`

// SelectInput / TextAreaInput render their label flush above the control; add
// an 8px gap below the label so their spacing matches this modal's own fields
// (Sport / Rate use fieldLabelStyle's 8px margin-bottom).
const builtinFieldStyle = css`
  .ant-select,
  .ant-input,
  textarea {
    margin-top: 8px;
  }
`

// Country + State/Province and City, stacked.
const locationStyle = css`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const rateStyle = css`
  flex: 0 0 110px;
`

// Required marker on the two fields isFormValid actually gates on. Inherits
// the label color with a 4px gap, matching TextInput's `required` asterisk.
const requiredMarkStyle = css`
  margin-left: 4px;
`
const fieldLabelStyle = css`
  ${paragraphSmall}
  ${weightSemiBold}
  ${font.body}
  color: ${colors.WHITE};
  margin-bottom: 8px;
`

// Match the SelectInput fields on this dark modal: SECONDARY_200 fill, white
// text, 40px height, subtle border — so Rate reads as the same input family.
// The wrapper carries the field's box so the currency prefix sits inside it.
const rateInputWrapStyle = css`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_200};
  background-color: ${colors.SECONDARY_200};
`
const rateCurrencyStyle = css`
  ${paragraphSmall}
  ${font.body}
  color: ${colors.WHITE};
  flex-shrink: 0;
`
const rateInputStyle = css`
  ${paragraphSmall}
  ${font.body}
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0;
  border: none;
  background-color: transparent;
  color: ${colors.WHITE};

  &:focus {
    outline: none;
  }

  /* Match the antd Select/TextArea default placeholder tone (muted gray), not
     the brighter NEUTRAL_50, so Rate reads the same as the other fields. */
  &::placeholder {
    color: ${colors.NEUTRAL_100};
  }
`

const errorTextStyle = css`
  ${paragraphSmall}
  ${font.body}
  color: ${colors.ERROR_200};
  margin-top: 4px;
`

const guidanceLinkStyle = css`
  color: ${colors.PRIMARY};
  text-decoration: underline;
  white-space: nowrap;
`

const footerStyle = css`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`
